import { PastureMapPanel } from "../PastureMapPanel";
import { managementTypeOption, managementGroupOption } from "../managementOptions";
import { useReportEffect } from "../../../../hooks/useReportEffect";
import { ReportTop } from "../../../../components/Dashboard/ReportLayout";
import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as BagFood } from "../../../../assets/svg/icons/bagFood.svg";
import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import Constants from "../../../../constants/Index";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MultiDropdownV2, OptionV2 } from "../../../../components/FormControlsV2";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import ChartBar from "../../../../components/Graph/Bar";
import ChartBarLine from "../../../../components/Graph/BarLine";
import "../styles.css"
import { IOptionDoubleValuesProps, IOptionProps, IOptionsProps } from "../../../../@types/reactSelect/IOption";

import { exportPastureManagementDataToExcel } from "../../../../utils/excel/exportPastureManagementData";
import { flattenSelection, useFilterContextV2, selectionToNumericIds, selectionToSortedIdsKey } from "../../../../contexts/FilterContextV2";

export default function DashboardManagementGeneral() {
    const { t } = useTranslation();
    const {
        rangeDays,
        setRangeDays,
        setHideMicroAreaFilter,
        setHideBatchFilter,
        setDashboard,
        setSideBarOptionSelected,
        setSelectRangeDate,
        snapshotMicroAreaMetrics,
        setSnapshotMicroAreaMetrics,
    } = useFilter();

    const selection = useFilterContextV2();
    const selectedFarms = useMemo(() => flattenSelection(selection.selectedFarms), [selection.selectedFarms]);
    const selectedAreas = useMemo(() => flattenSelection(selection.selectedAreas).filter(area =>
        selectedFarms.some(farm => String(farm.value) === String(area.info?.farm?.id))), [selection.selectedAreas, selectedFarms]);
    const [focusedFarmId, setFocusedFarmId] = useState<string | null>(null);


    const farmsKey = useMemo(
        () => selectionToSortedIdsKey(selectedFarms),
        [selectedFarms],
    );
    const areasKey = useMemo(
        () => selectionToSortedIdsKey(selectedAreas),
        [selectedAreas],
    );

    const {
        handleSubmit,
        control,
        watch,
        setValue,
    } = useForm();

    const [optionsTypeManagement, setOptionsTypeManagement] = useState<IOptionsProps>([]);
    const [managementOptions, setManagementOptions] = useState<IOptionsProps>();
    const [lotInst, setLotInst] = useState<IOptionsProps>([]);
    const [consumoSuplemment, setConsumoSuplemment] = useState<IOptionDoubleValuesProps>();
    const [prodAcumulada, setProdAcumulada] = useState<IOptionsProps>([]);
    const [microAreas, setMicroAreas] = useState<IMicroArea[]>();
    const [microAreasSelected, setMicroAreasSelected] = useState<number[]>([]);

    const typeManagement = watch("managementType")
    const groupManagement = watch("managementGroup")

    useEffect(() => {
        setValue("managementGroup", [])
    }, [farmsKey, areasKey, typeManagement, setValue]);

    useEffect(() => {
        setHideMicroAreaFilter(true)
        setHideBatchFilter(true)
        setSideBarOptionSelected("DASHBOARD")
        setDashboard("MANAGEMENT")
        setSelectRangeDate(0)
        document.title = Constants.TITLE_DASHBOARD;
        const now = new Date();
        const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        setRangeDays({
            from: firstDayOfLastMonth,
            to: firstDayOfCurrentMonth
        })
    }, []);

  useReportEffect((axios) => {
    axios
      .get("/micro-area-management/types")
      .then((response) => {
        const types: IOptionsProps = response.data.map(managementTypeOption)
        setOptionsTypeManagement(types.filter((type: any) => type.value != 1))
      }).catch(() => undefined)
    axios.get("/micro-areas").then(response => setMicroAreas(response.data)).catch(() => undefined)
  }, []);

  useReportEffect((axios) => {
    setManagementOptions([]);
    const farmIds = selectionToNumericIds(selectedFarms);
    const areaIds = selectionToNumericIds(selectedAreas);
    if (!rangeDays || !farmIds.length || !areaIds.length) return;
    const params = new URLSearchParams({
      startDate: format(rangeDays.from || new Date(), 'yyyy-MM-dd'),
      endDate: format(rangeDays.to || new Date(), 'yyyy-MM-dd'),
    });
    farmIds.forEach(id => params.append("farmIds", String(id)));
    areaIds.forEach(id => params.append("areaIds", String(id)));
    axios.get<IAnalyticsManagementGroupsByFarm[]>(`/analytics/snapshots/management-groups?${params}`)
      .then(({ data }) => setManagementOptions(data.map(group => {
        const option = managementGroupOption(group);
        return {
          ...option,
          value: `${group.farm.id}:${option.value}`,
          label: `${group.farm.name} · ${option.label}`,
          info: { ...group, farmId: String(group.farm.id), farmName: group.farm.name },
        };
      }))).catch(() => undefined);
  }, [rangeDays, farmsKey, areasKey]);

  useReportEffect((axios) => {
    setSnapshotMicroAreaMetrics(undefined as any);
    if (!rangeDays || !groupManagement || groupManagement.length === 0 || !selectedAreas.length) {
      setSnapshotMicroAreaMetrics(undefined as any);
      return;
    }

    const controller = new AbortController();

    const fetchMetrics = async () => {
      const startDate = format(rangeDays.from ? new Date(rangeDays.from) : new Date(), 'yyyy-MM-dd');
      const endDate = format(rangeDays.to ? new Date(rangeDays.to) : new Date(), 'yyyy-MM-dd');

      const microAreas = groupManagement
        .map((management: IOptionProps) =>
          (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)
        )
        .flat();

      if (microAreas.length === 0) {
        setSnapshotMicroAreaMetrics(undefined as any);
        return;
      }

      try {
        const microAreaParams = microAreas.map((microArea: number) => `microAreaIds=${microArea}`).join("&");
        const response = await axios.get(
          `/analytics/snapshots/micro-areas/metrics?startDate=${startDate}&endDate=${endDate}&${microAreaParams}`,
          { signal: controller.signal }
        );
        setSnapshotMicroAreaMetrics(response.data);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Erro ao carregar métricas de manejo:', error);
        }
      }
    };

    fetchMetrics();

    return () => controller.abort();
  }, [rangeDays, groupManagement, farmsKey, areasKey]);

    useEffect(() => {
        if (!snapshotMicroAreaMetrics) {
            setLotInst([]);
            setConsumoSuplemment(undefined);
            setProdAcumulada([]);
            return;
        }
        if (snapshotMicroAreaMetrics) {
            setLotInst(snapshotMicroAreaMetrics.daily_metrics.map((inst) => ({
                label: inst.date,
                value: inst.average_instant_capacity_rate
            })))

            setConsumoSuplemment({
                data1: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_consumption_kg_per_animal
                })),
                data2: snapshotMicroAreaMetrics.daily_metrics.map(inst => ({
                    label: inst.date,
                    value: inst.average_consumption_g_per_kg_pv
                }))
            })

            setProdAcumulada(snapshotMicroAreaMetrics.daily_metrics.map((inst) => ({
                label: inst.date,
                value: inst.productivity
            })))
        }
    }, [snapshotMicroAreaMetrics]);

    const renderManagementGroupOption = (option: OptionV2) => {
        const infoOption: IAnalyticsManagementGroups = option.info;

        return (
            <div className="pasture-group-option">
                {option.label}
                <br />
                {infoOption.micro_areas.length > 0 ? infoOption.micro_areas.map(microArea => microArea.initials).join(" ") : ""}
            </div>
        );
    }

    const focusManagementGroup = (option: OptionV2) => setFocusedFarmId(option.info.farmId);

    useEffect(() => {
        setMicroAreasSelected((groupManagement || []).flatMap((management: IOptionProps) => (management.info as IAnalyticsManagementGroups).micro_areas.map(microArea => microArea.id)))
    }, [groupManagement]);

    return (
        <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
            <DateModal />

            <form onSubmit={() => handleSubmit(() => { })} className="dashboardPastureManagement dashboardPastureManagement--multi-farm">
                <div className="cattleMap_page">
            <ReportTop kind="management" multipleManagementFarms icon={<BagFood height={72} />} title={t("navigation.pastureManagement")} subtitle={t("reports.generalView")}>
              <div>
                <div className="pasture-period-filter">
                <FilterDateDashboard
                  onExportExcel={() => {
                    if (snapshotMicroAreaMetrics) {
                      try {
                        exportPastureManagementDataToExcel(
                          snapshotMicroAreaMetrics,
                          rangeDays?.from ? new Date(rangeDays.from) : undefined,
                          rangeDays?.to ? new Date(rangeDays.to) : undefined
                        );
                      } catch (error) {
                        console.error('Erro ao exportar:', error);
                        alert(t("reports.exportError"));
                      }
                    }
                  }}
                />
                </div>
                <br />
                <div className="management-filters-layout">
                  <div className="management-filters-fields">
                  <Controller
                    name="managementType"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <MultiDropdownV2
                        name="managementType"
                        label={t("reports.grazingType")}
                        placeholder={t("reports.select")}
                        menuMount="static"
                        emptyMessage={t("reports.noOptions")}
                        options={optionsTypeManagement}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={error?.message}
                      />
                    )}
                  />

                  <Controller
                    name="managementGroup"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <MultiDropdownV2
                        name="managementGroup"
                        label={t("reports.managementGroup")}
                        placeholder={t("reports.select")}
                        menuMount="static"
                        emptyMessage={t("reports.noOptions")}
                        options={typeManagement?.length ? (managementOptions || []).filter(option => typeManagement.some((type: IOptionProps) => String(type.value) === String(option.info.management_type.id))) : []}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={error?.message}
                        renderOption={renderManagementGroupOption}
                        onOptionFocus={focusManagementGroup}
                        searchable
                        selectAll
                      />
                    )}
                  />

                  </div>

                  <PastureMapPanel
                    selectedFarms={selectedFarms}
                    focusedFarmId={focusedFarmId}
                    setFocusedFarmId={setFocusedFarmId}
                    microAreas={microAreas}
                    microAreasSelected={microAreasSelected}
                  />
                </div>
              </div>
            </ReportTop>
                </div>

                <br />
                <div className="splitContent">
                    {/* <ChartBarLineAverage
                            titleGraph={t("reports.instantaneousStocking")}
                            dataColumn={lotInst}
                            average={lotInst.length > 0 ? lotInst.reduce((acc, item) => acc + item.value, 0) / lotInst.length : 0}
                            colorIcons={[t("reports.instantaneousStocking"), t("reports.average")]}
                            axisLabels={"UA/ha"}
                            fullWidth
                        /> */}
                    <div style={{
                        overflowX: "auto",
                        width: "100%"
                    }}>
                        <ChartBar
                            titleGraph={t("reports.instantaneousStocking")}
                            data={lotInst}
                            axisLabels={["", "UA/ha"]}
                            fullWidth
                            monoColor
                        />
                    </div>
                    {lotInst.length > 0 && !lotInst?.every((el: any) => el?.value === 0) ?
                        <div className="contentGraphBorder" style={{ minWidth: 200, maxWidth: 980, marginTop: 21, flexDirection: "column" }}>
                            <span className="infoMediaContainer">
                                {t("reports.averageStockingPeriod")}
                                <br />
                                <br />
                                {(lotInst.reduce((acc, item) => acc + item.value, 0) / lotInst.length).toFixed(2)} UA/ha
                            </span>
                        </div>
                        : <></>
                    }
                </div>

                <div className="splitContent">
                    <div style={{
                        overflowX: "auto",
                        width: "100%"
                    }}>
                        <ChartBarLine
                            titleGraph={t("reports.supplementConsumption")}
                            dataColumn={consumoSuplemment?.data1 || []}
                            dataLine={consumoSuplemment?.data2 || []}
                            colorIcons={[t("reports.averageConsumptionKg"), t("reports.averageConsumptionAnimal")]}
                            axisLabels={["", "g/kgPV.dia", "kg/cab.dia"]}
                            fullWidth
                        />
                    </div>
                    {consumoSuplemment && !consumoSuplemment?.data1.every((el: any) => el?.value === 0) && !consumoSuplemment?.data2.every((el: any) => el?.value === 0) ?
                        <div className="contentGraphBorder" style={{ minWidth: 200, maxWidth: 980, marginTop: 21, flexDirection: "column" }}>
                            <span className="infoMediaContainer">
                                {t("reports.averageConsumptionPeriod")}
                                <br />
                                <br />
                                {!consumoSuplemment?.data1.every((el: any) => el?.value === 0) ? `${(consumoSuplemment?.data1.reduce((acc, item) => acc + item.value, 0) / consumoSuplemment?.data1.length).toFixed(2)} g/kgPV.dia` : <></>}
                                <br />
                                {!consumoSuplemment?.data1.every((el: any) => el?.value === 0) ? `${(consumoSuplemment?.data2.reduce((acc, item) => acc + item.value, 0) / consumoSuplemment?.data2.length).toFixed(2)} kg/cab.dia` : <></>}
                            </span>
                        </div>
                        : <></>
                    }
                </div>

                <div className="splitContent">
                    <div style={{
                        overflowX: "auto",
                        width: "100%"
                    }}>
                        <ChartBar
                            titleGraph={t("reports.accumulatedProductivity")}
                            data={prodAcumulada}
                            // colorIcons={"Produtividade acumulada"}
                            axisLabels={["", "Kg PV/ha"]}
                            fullWidth
                            monoColor
                        />
                    </div>
                    {prodAcumulada.length > 0 && !prodAcumulada?.every((el: any) => el?.value === 0) ?
                        <div className="contentGraphBorder" style={{ minWidth: 200, maxWidth: 980, marginTop: 21, flexDirection: "column" }}>
                            <span className="infoMediaContainer">
                                {t("reports.totalProductivityPeriod")}
                                <br />
                                <br />
                                {(prodAcumulada.reduce((acc, item) => acc + item.value, 0)).toFixed(2)} Kg PV/ha
                            </span>
                        </div>
                        : <></>
                    }
                </div>
            </form >
        </div >
    );
}
